import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Download, Mail, MailOpen, Trash2, ExternalLink, Search, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import useApi from "@/hooks/useApi";
import CrudManager from "@/components/admin/CrudManager";
import { brandSlugByName } from "@/content/brands";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const useReload = (path) => {
  const [key, setKey] = useState(0);
  const res = useApi(path, [key]);
  return { ...res, reload: () => setKey((k) => k + 1) };
};

/**
 * A reusable admin filter bar: one text search + N select filters.
 * `filters` is an array of { key, label, options: [{value,label}], value, onChange }.
 */
const FilterBar = ({
  search,
  onSearch,
  searchPlaceholder = "Search…",
  filters = [],
  onClear,
  count,
  total,
}) => (
  <div data-testid="filter-bar" className="mb-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
    <div className="relative flex-1 min-w-[220px]">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder={searchPlaceholder}
        data-testid="filter-search"
        className="pl-9 bg-background border-border rounded-none"
      />
    </div>
    {filters.map((f) => (
      <Select key={f.key} value={f.value || "all"} onValueChange={(v) => f.onChange(v === "all" ? "" : v)}>
        <SelectTrigger data-testid={`filter-${f.key}`} className="w-full md:w-[200px] bg-background border-border rounded-none">
          <SelectValue placeholder={f.label} />
        </SelectTrigger>
        <SelectContent className="bg-popover border-border">
          <SelectItem value="all">{`All ${f.label.toLowerCase()}`}</SelectItem>
          {f.options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ))}
    <div className="flex items-center gap-3 md:ml-auto shrink-0">
      <span data-testid="filter-count" className="font-mono text-xs uppercase tracking-widest text-muted-foreground whitespace-nowrap">
        {count} of {total}
      </span>
      {(search || filters.some((f) => f.value)) && (
        <button
          type="button"
          onClick={onClear}
          data-testid="filter-clear"
          className="inline-flex items-center gap-1 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-destructive"
        >
          <X size={12} /> Clear
        </button>
      )}
    </div>
  </div>
);

const includes = (needle) => (hay) => (hay || "").toLowerCase().includes((needle || "").toLowerCase());

const Overview = () => {
  const { data } = useApi("/admin/stats");
  const cards = [
    { k: "brands", label: "Brands" },
    { k: "leaders", label: "Leadership" },
    { k: "jobs", label: "Open Jobs" },
    { k: "news", label: "News Posts" },
    { k: "contacts", label: "Enquiries" },
    { k: "subscribers", label: "Subscribers" },
  ];
  return (
    <div>
      <h2 className="font-serif text-3xl md:text-4xl font-light mb-8">Overview</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
        {cards.map((c) => (
          <div key={c.k} className="bg-card p-8" data-testid={`stat-${c.k}`}>
            <p className="font-serif text-5xl md:text-6xl font-light text-primary">
              {data ? data[c.k] : "—"}
            </p>
            <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-3">
              {c.label}
            </p>
          </div>
        ))}
      </div>
      {data?.unread_contacts > 0 && (
        <p className="mt-6 font-mono text-xs uppercase tracking-widest text-primary">
          {data.unread_contacts} unread enquir{data.unread_contacts === 1 ? "y" : "ies"}
        </p>
      )}
    </div>
  );
};

const Contacts = () => {
  const { data, reload } = useReload("/admin/contacts");
  const all = (data || []).filter((c) => c.inquiry_type !== "Apply for a job");

  const [search, setSearch] = useState("");
  const [readState, setReadState] = useState("");
  const [inquiryType, setInquiryType] = useState("");
  const [brand, setBrand] = useState("");

  const list = useMemo(() => {
    const m = includes(search);
    return all.filter((c) => {
      if (search && !(m(c.name) || m(c.email) || m(c.message) || m(c.brand))) return false;
      if (readState === "unread" && c.read) return false;
      if (readState === "read" && !c.read) return false;
      if (inquiryType && c.inquiry_type !== inquiryType) return false;
      if (brand && c.brand !== brand) return false;
      return true;
    });
  }, [all, search, readState, inquiryType, brand]);

  const brandOptions = useMemo(
    () => Array.from(new Set(all.map((c) => c.brand).filter(Boolean))).sort().map((b) => ({ value: b, label: b })),
    [all],
  );
  const inquiryOptions = useMemo(
    () => Array.from(new Set(all.map((c) => c.inquiry_type).filter(Boolean))).sort().map((t) => ({ value: t, label: t })),
    [all],
  );

  const clearFilters = () => {
    setSearch(""); setReadState(""); setInquiryType(""); setBrand("");
  };

  const markRead = async (id) => {
    await api.put(`/admin/contacts/${id}/read`);
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div data-testid="contacts-manager">
      <h2 className="font-serif text-3xl md:text-4xl font-light mb-8">Enquiries</h2>
      <FilterBar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search name, email, message or brand…"
        filters={[
          {
            key: "read",
            label: "Status",
            value: readState,
            onChange: setReadState,
            options: [
              { value: "unread", label: "Unread only" },
              { value: "read", label: "Read only" },
            ],
          },
          { key: "type", label: "Type", value: inquiryType, onChange: setInquiryType, options: inquiryOptions },
          { key: "brand", label: "Brand", value: brand, onChange: setBrand, options: brandOptions },
        ]}
        onClear={clearFilters}
        count={list.length}
        total={all.length}
      />
      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["", "Name", "Brand", "Type", "Message", "CV", "Date", ""].map((h, i) => (
                <TableHead key={i} className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((c) => (
              <TableRow key={c.id} className="border-border" data-testid={`contact-row-${c.id}`}>
                <TableCell>
                  {c.read ? (
                    <MailOpen size={16} className="text-muted-foreground" />
                  ) : (
                    <Mail size={16} className="text-primary" />
                  )}
                </TableCell>
                <TableCell className="font-light">
                  <div>{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.email}</div>
                </TableCell>
                <TableCell className="font-light">{c.brand}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-primary/40 text-primary rounded-none font-mono text-[10px] uppercase">
                    {c.inquiry_type}
                  </Badge>
                </TableCell>
                <TableCell className="font-light max-w-sm truncate">{c.message}</TableCell>
                <TableCell className="font-mono text-xs">
                  {c.cv_stored_name ? (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const res = await api.get(`/admin/cv/${c.id}`, { responseType: "blob" });
                          const blob = new Blob([res.data]);
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = c.cv_filename || "cv";
                          document.body.appendChild(a); a.click(); a.remove();
                          URL.revokeObjectURL(url);
                        } catch {
                          toast.error("CV download failed.");
                        }
                      }}
                      data-testid={`cv-download-${c.id}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      title={c.cv_filename || "CV"}
                    >
                      <Download size={14} /> CV
                    </button>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {(c.created_at || "").slice(0, 10)}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {!c.read && (
                    <button onClick={() => markRead(c.id)} className="text-muted-foreground hover:text-primary p-2" data-testid={`read-${c.id}`}>
                      <MailOpen size={16} />
                    </button>
                  )}
                  <button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive p-2" data-testid={`del-contact-${c.id}`}>
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-10 text-muted-foreground font-light">
                  {all.length === 0 ? "No enquiries yet." : "No enquiries match these filters."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const Subscribers = () => {
  const { data } = useReload("/admin/subscribers");
  const list = data || [];

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "whichmedia_subscribers.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded — ready for Mailchimp.");
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="subscribers-manager">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light">Newsletter Subscribers</h2>
        <Button
          onClick={exportCsv}
          data-testid="export-csv"
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono text-xs uppercase tracking-widest"
        >
          <Download size={16} className="mr-2" /> Export CSV
        </Button>
      </div>
      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["Email", "Brands", "Subscribed"].map((h) => (
                <TableHead key={h} className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  {h}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((s) => (
              <TableRow key={s.id} className="border-border" data-testid={`sub-row-${s.id}`}>
                <TableCell className="font-light">{s.email}</TableCell>
                <TableCell className="font-light text-sm text-muted-foreground">
                  {(s.brands || []).join(", ")}
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {(s.created_at || "").slice(0, 10)}
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-10 text-muted-foreground font-light">
                  No subscribers yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const BrandsTab = () => {
  const { data, reload } = useReload("/brands");
  return (
    <CrudManager
      title="Brands"
      endpoint="brands"
      items={data}
      reload={reload}
      previewUrl={(item, form) => {
        // Prefer the DB name-slug mapping; fall back to the edited form name for
        // new/renamed brands.
        const name = (item && item.name) || (form && form.name);
        const slug = name ? brandSlugByName[name] : null;
        return slug ? `/brands/${slug}` : null;
      }}
      columns={[
        { key: "name", label: "Name" },
        { key: "category", label: "Category" },
        { key: "status", label: "Status" },
        { key: "order", label: "Order" },
      ]}
      fields={[
        { key: "name", label: "Name", type: "text" },
        { key: "domain", label: "Domain", type: "text" },
        { key: "tagline", label: "Tagline", type: "text" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "category", label: "Category", type: "text" },
        { key: "status", label: "Status", type: "select", options: ["live", "coming_soon"] },
        { key: "url", label: "URL", type: "text" },
        { key: "image", label: "Card image", type: "image", hint: "Shown on the Home + Our Brands grids. JPG, PNG, WEBP or GIF · under 8 MB. You can also paste an external URL." },
        { key: "hero_image", label: "Brand-page hero image", type: "image", hint: "The masthead image on the individual brand page. Leave blank to fall back to the Card image." },
        { key: "hero_video", label: "Brand-page hero video (embed URL)", type: "text", hint: "Optional. e.g. https://player.vimeo.com/video/XXXXX?autoplay=1&loop=1&muted=1 — shown as the 'Watch the film' section." },
        { key: "logo", label: "Logo path (e.g. /logos/x.png)", type: "text" },
        { key: "logo_dark_bg", label: "Logo needs dark background", type: "checkbox", hint: "Show logo on dark block", default: false },
        {
          key: "hero_images",
          label: "Hero image triptych (JSON array of URLs)",
          type: "json",
          rows: 5,
          jsonDefault: [],
          placeholder: '[\n  "/reports/hero-1.png",\n  "/reports/hero-2.png",\n  "/reports/hero-3.png"\n]',
          hint: "Optional. If provided, replaces the single hero image with a 3-up triptych (used by the Education Intelligence Group page).",
        },
        {
          key: "hero_animation",
          label: "Hero image animation (JSON)",
          type: "json",
          rows: 4,
          jsonDefault: null,
          placeholder: '{ "origin": "63% 8%", "scale": 1.45, "duration": 10 }',
          hint: "Optional Ken-Burns tween applied to the hero. Leave blank for default gentle zoom.",
        },
        {
          key: "stats",
          label: "Stats band (JSON array of {value, label})",
          type: "json",
          rows: 10,
          jsonDefault: [],
          placeholder: '[\n  { "value": "2013", "label": "Launched" },\n  { "value": "7", "label": "International markets" }\n]',
          hint: "Six stats display best. Rendered under the hero on the brand page.",
        },
        {
          key: "body",
          label: "Body paragraphs (JSON array of strings)",
          type: "json",
          rows: 12,
          jsonDefault: [],
          placeholder: '[\n  "First paragraph...",\n  "Second paragraph..."\n]',
          hint: "The long-form 'About the brand' copy. Each string is one paragraph.",
        },
        {
          key: "access",
          label: "How-to-access block (JSON)",
          type: "json",
          rows: 10,
          jsonDefault: null,
          placeholder: '{\n  "title": "How to access …",\n  "intro": "…",\n  "steps": [{ "text": "…", "url": "https://…", "urlLabel": "…" }]\n}',
          hint: "Optional. Numbered access-steps section (used by EdinformaPlus). Leave blank to hide.",
        },
        {
          key: "reports",
          label: "Free Reports (JSON array)",
          type: "json",
          rows: 10,
          jsonDefault: [],
          placeholder: '[\n  { "title": "Report title", "blurb": "Short blurb", "cover": "/reports/cover.png", "file": "/reports/file.pdf" }\n]',
          hint: 'Optional. Each entry: { "title", "blurb", "cover", "file" }. PDFs and cover images should live under /app/frontend/public/reports/.',
        },
        {
          key: "ad_guidelines",
          label: "Ad guidelines (JSON)",
          type: "json",
          rows: 14,
          jsonDefault: null,
          placeholder: '{\n  "intro": "…",\n  "tech": { "maxFile": "10 MB", "formats": "JPG · GIF · PNG", "responsive": "…" },\n  "jumbo": [{ "size": "1", "upload": "656 × 544", "display": "328 × 272", "minScreen": "360" }],\n  "elegant": [ ... ],\n  "placements": [{ "name": "Leaderboard", "profile": "Elegant" }],\n  "flexible": "…"\n}',
          hint: "Optional. Full Ad-Sizes block. WhichSchoolAdvisor uses tech + jumbo only; SchoolsCompared adds placements, elegant and flexible.",
        },
        {
          key: "instagram",
          label: "Instagram widgets (JSON)",
          type: "json",
          rows: 8,
          jsonDefault: [],
          placeholder: '[\n  { "region": "Global", "handle": "whichschooladvisor", "widgetUrl": "https://cdn.lightwidget.com/widgets/xxxx.html" }\n]',
          hint: 'Array of Instagram sources shown on the brand page. Each item: { "region", "handle", "widgetUrl" }. Paste the LightWidget embed URL (the src of the LightWidget iframe). Leave the array empty to hide the module.',
        },
        { key: "order", label: "Order", type: "number" },
      ]}
    />
  );
};

const LeadersTab = () => {
  const { data, reload } = useReload("/leaders");
  return (
    <CrudManager
      title="Leadership"
      endpoint="leaders"
      items={data}
      reload={reload}
      columns={[
        { key: "name", label: "Name" },
        { key: "role", label: "Role" },
        { key: "order", label: "Order" },
      ]}
      fields={[
        { key: "name", label: "Name", type: "text" },
        { key: "role", label: "Role", type: "text" },
        { key: "bio", label: "Bio", type: "textarea" },
        { key: "email", label: "Email", type: "text" },
        { key: "contact_for", label: "Contact For", type: "text" },
        { key: "image", label: "Image URL (optional)", type: "text" },
        { key: "order", label: "Order", type: "number" },
      ]}
    />
  );
};

const JobsTab = () => {
  const { data, reload } = useReload("/jobs?all=true");
  return (
    <CrudManager
      title="Jobs"
      endpoint="jobs"
      items={data}
      reload={reload}
      columns={[
        { key: "title", label: "Title" },
        { key: "department", label: "Dept" },
        { key: "location", label: "Location" },
        { key: "active", label: "Status", render: (v, row) => (row.closed ? "Closed" : v ? "Live" : "Hidden") },
      ]}
      fields={[
        { key: "title", label: "Title", type: "text" },
        { key: "department", label: "Department", type: "text" },
        { key: "location", label: "Location", type: "text" },
        { key: "type", label: "Type", type: "select", options: ["Full-time", "Part-time", "Contract", "Remote"] },
        { key: "description", label: "Description", type: "textarea", hint: "Formatting supported: blank line = new paragraph, single return = line break. Links are clickable — paste a URL or use [link text](https://url)." },
        { key: "closed", label: "Closed", type: "checkbox", hint: "Stop taking new applications — the ad stays on the careers page with a Closed sign and the Apply button is removed", default: false },
        { key: "active", label: "Active", type: "checkbox", hint: "Show on careers page", default: true },
      ]}
    />
  );
};

const NewsTab = () => {
  const { data, reload } = useReload("/news?all=true");
  return (
    <CrudManager
      title="News"
      endpoint="news"
      items={data}
      reload={reload}
      previewUrl={(item, form) => {
        // Prefer saved slug; fall back to unsaved form slug so new posts can preview too.
        const slug = (form && form.slug) || (item && item.slug);
        return slug ? `/news/${slug}` : null;
      }}
      columns={[
        { key: "title", label: "Title" },
        { key: "category", label: "Category" },
        { key: "date", label: "Date" },
        { key: "published", label: "Live", render: (v) => (v ? "Yes" : "No") },
      ]}
      fields={[
        { key: "title", label: "Title", type: "text" },
        { key: "category", label: "Category", type: "select", options: ["Announcement", "Press", "Research", "Insight"] },
        { key: "date", label: "Date (YYYY-MM-DD)", type: "text" },
        { key: "slug", label: "URL slug (leave blank to auto-generate)", type: "text", hint: "Lower-case, hyphenated — appears as /news/<slug>" },
        { key: "excerpt", label: "Excerpt", type: "textarea" },
        { key: "body", label: "Body", type: "ai-textarea", rows: 10, aiPrompt: "Take this copy and rework into a constructive, objective news story of 240 words. Automatically italicise key phrases through the copy.", hint: "Formatting: blank line = new paragraph, single return = line break. Links are clickable — paste a URL or use [link text](https://url). Italic/bold via <em> and <strong>." },
        { key: "image", label: "Image", type: "image", hint: "JPG, PNG, WEBP or GIF · under 8 MB. You can also paste an external URL." },
        { key: "published", label: "Published", type: "checkbox", hint: "Visible on site", default: true },
      ]}
    />
  );
};

const APP_STATUSES = ["New", "Reviewed", "Shortlisted", "Interviewed", "Offer", "Hired", "Rejected"];
const APP_STATUS_COLOR = {
  New: "bg-primary text-primary-foreground",
  Reviewed: "bg-sky-100 text-sky-800",
  Shortlisted: "bg-amber-100 text-amber-800",
  Interviewed: "bg-indigo-100 text-indigo-800",
  Offer: "bg-purple-100 text-purple-800",
  Hired: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-700",
};

const downloadCv = async (c) => {
  try {
    const res = await api.get(`/admin/cv/${c.id}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = c.cv_filename || "cv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("CV download failed.");
  }
};

const Applications = () => {
  const { data, reload } = useReload("/admin/contacts");
  const all = (data || []).filter((c) => c.inquiry_type === "Apply for a job");
  const [active, setActive] = useState(null);
  const [status, setStatus] = useState("New");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cvFilter, setCvFilter] = useState("");

  const list = useMemo(() => {
    const m = includes(search);
    return all.filter((c) => {
      if (search && !(m(c.name) || m(c.email) || m(c.message))) return false;
      if (statusFilter && (c.application_status || "New") !== statusFilter) return false;
      if (cvFilter === "yes" && !c.cv_stored_name) return false;
      if (cvFilter === "no" && c.cv_stored_name) return false;
      return true;
    });
  }, [all, search, statusFilter, cvFilter]);

  const clearFilters = () => { setSearch(""); setStatusFilter(""); setCvFilter(""); };

  const openApp = (c) => {
    setActive(c);
    setStatus(c.application_status || "New");
    setNotes(c.application_notes || "");
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/applications/${active.id}`, { status, notes });
      toast.success("Application updated");
      setActive(null);
      reload();
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Deleted");
    reload();
  };

  const counts = APP_STATUSES.map((s) => ({ s, n: all.filter((c) => (c.application_status || "New") === s).length }));

  return (
    <div data-testid="applications-manager">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-light">Applications</h2>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-2">
            {all.length} total · {counts.filter((c) => c.n > 0).map((c) => `${c.n} ${c.s}`).join(" · ") || "no applications yet"}
          </p>
        </div>
      </div>

      <FilterBar
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search applicant name, email or message…"
        filters={[
          {
            key: "status",
            label: "Status",
            value: statusFilter,
            onChange: setStatusFilter,
            options: APP_STATUSES.map((s) => ({ value: s, label: s })),
          },
          {
            key: "cv",
            label: "CV",
            value: cvFilter,
            onChange: setCvFilter,
            options: [
              { value: "yes", label: "With CV" },
              { value: "no", label: "Without CV" },
            ],
          },
        ]}
        onClear={clearFilters}
        count={list.length}
        total={all.length}
      />

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["Applicant", "Role / Message", "Status", "CV", "Received", ""].map((h, i) => (
                <TableHead key={i} className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((c) => {
              const s = c.application_status || "New";
              return (
                <TableRow key={c.id} className="border-border cursor-pointer" data-testid={`app-row-${c.id}`} onClick={() => openApp(c)}>
                  <TableCell className="font-light">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-muted-foreground">{c.email}</div>
                  </TableCell>
                  <TableCell className="font-light max-w-md">
                    <div className="line-clamp-2 text-sm">{c.message}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${APP_STATUS_COLOR[s] || "bg-secondary"}`}>{s}</span>
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {c.cv_stored_name ? (
                      <button type="button" onClick={(e) => { e.stopPropagation(); downloadCv(c); }} data-testid={`app-cv-${c.id}`} className="inline-flex items-center gap-1 text-primary hover:underline">
                        <Download size={14} /> CV
                      </button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">{(c.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button onClick={(e) => { e.stopPropagation(); del(c.id); }} data-testid={`app-del-${c.id}`} className="text-muted-foreground hover:text-destructive p-2"><Trash2 size={16} /></button>
                  </TableCell>
                </TableRow>
              );
            })}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-muted-foreground font-light">
                  {all.length === 0 ? "No applications yet." : "No applications match these filters."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="application-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">{active?.name}</DialogTitle>
            <DialogDescription className="font-mono text-xs text-muted-foreground">
              {active?.email} · {(active?.created_at || "").slice(0, 10)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Message</label>
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/80">{active?.message}</p>
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="application-status" className="mt-2 bg-background border-border rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border">
                  {APP_STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Notes (private)</label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="application-notes"
                className="mt-2 bg-background border-border rounded-none"
                rows={5}
                placeholder="Interview thoughts, next steps, links to work…"
              />
            </div>
            {active?.cv_stored_name && (
              <div>
                <Button onClick={() => downloadCv(active)} data-testid="dialog-cv-download" className="bg-ink text-white hover:bg-primary rounded-none font-mono text-xs uppercase tracking-widest">
                  <Download size={14} className="mr-2" /> Download CV ({active.cv_filename})
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="application-save"
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono text-xs uppercase tracking-widest"
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Backups = () => {
  const { data, reload } = useReload("/admin/backups");
  const list = data || [];
  const [busy, setBusy] = useState(false);

  const humanSize = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(2)} GB`;
  };

  const runBackup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backups");
      toast.success(`Backup created: ${data.filename}`);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Backup failed");
    } finally {
      setBusy(false);
    }
  };

  const download = async (name) => {
    try {
      const res = await api.get(`/admin/backups/${name}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/gzip" }));
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const del = async (name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    await api.delete(`/admin/backups/${name}`);
    toast.success("Deleted");
    reload();
  };

  return (
    <div data-testid="backups-manager">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <h2 className="font-serif text-3xl md:text-4xl font-light">Database Backups</h2>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground mt-2">
            Automated daily · rolling 14-archive rotation · gzipped mongodump
          </p>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
            The server takes a compressed backup of the entire database once a day and keeps the last 14. Download any archive to your local
            machine for true off-server safety. Restore with:{" "}
            <code className="font-mono text-xs bg-secondary px-2 py-1">
              mongorestore --archive=file.archive.gz --gzip
            </code>
          </p>
        </div>
        <Button
          onClick={runBackup}
          disabled={busy}
          data-testid="backup-now"
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none font-mono text-xs uppercase tracking-widest disabled:opacity-40"
        >
          {busy ? "Backing up…" : "Back up now"}
        </Button>
      </div>

      <div className="border border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              {["Filename", "Size", "Created", ""].map((h, i) => (
                <TableHead key={i} className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((b) => (
              <TableRow key={b.filename} className="border-border" data-testid={`backup-row-${b.filename}`}>
                <TableCell className="font-mono text-xs">{b.filename}</TableCell>
                <TableCell className="font-light">{humanSize(b.size)}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {(b.modified_at || "").replace("T", " ").slice(0, 19)} UTC
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => download(b.filename)} data-testid={`backup-download-${b.filename}`} className="text-primary hover:underline inline-flex items-center gap-1 mr-4 font-mono text-xs">
                    <Download size={14} /> Download
                  </button>
                  <button onClick={() => del(b.filename)} data-testid={`backup-del-${b.filename}`} className="text-muted-foreground hover:text-destructive p-2">
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-10 text-muted-foreground font-light">
                  No backups yet — click <span className="font-mono">Back up now</span> to create the first one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const saveBlob = (data, filename) => {
  const url = URL.createObjectURL(new Blob([data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
};

const downloadAdZip = async (s) => {
  try {
    const res = await api.get(`/admin/ad-submissions/${s.id}/download.zip`, { responseType: "blob" });
    const slug = (s.company || "campaign").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    saveBlob(res.data, `${slug}-creatives.zip`);
  } catch {
    toast.error("Download failed.");
  }
};

const downloadAdFile = async (s, idx, filename) => {
  try {
    const res = await api.get(`/admin/ad-submissions/${s.id}/files/${idx}`, { responseType: "blob" });
    saveBlob(res.data, filename || "creative");
  } catch {
    toast.error("Download failed.");
  }
};

/** Authenticated thumbnail for one creative in a campaign (files are
 * admin-only, so we fetch the bytes with the token and render an object URL). */
const AdThumb = ({ subId, idx, alt }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    let url = null;
    api.get(`/admin/ad-submissions/${subId}/files/${idx}`, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(new Blob([res.data]));
        setSrc(url);
      })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [subId, idx]);
  if (!src) return <div className="h-14 w-24 rounded border border-border bg-secondary animate-pulse" />;
  return (
    <img
      src={src}
      alt={alt}
      data-testid={`ad-thumb-${subId}-${idx}`}
      className="h-14 rounded border border-border bg-secondary object-contain"
    />
  );
};

const SITE_LABELS = { wsa: "WhichSchoolAdvisor.com", sc: "SchoolsCompared.com", both: "Both sites" };

const subFiles = (s) =>
  s.files?.length
    ? s.files
    : s.stored_name
      ? [{ filename: s.filename, width: s.width, height: s.height, matched_size: s.matched_size,
           format: s.format, frames: s.frames, size_bytes: s.size_bytes, checks: s.checks || [] }]
      : [];

const AdUploadsTab = () => {
  const { data, reload } = useReload("/admin/ad-submissions");
  const list = data || [];

  const del = async (s) => {
    if (!window.confirm(`Delete the ad from ${s.name} (${s.filename})? The file is removed permanently.`)) return;
    try {
      await api.delete(`/admin/ad-submissions/${s.id}`);
      toast.success("Submission deleted.");
      reload();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="ad-uploads-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-3xl">Ad uploads</h2>
        <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">{list.length} received</span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
        Creatives submitted via the advertising page — every file here has already passed the format, weight, frame and dimension rules
      </p>
      <div className="bg-card border border-border rounded-xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest">From</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest">Creative</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest">Checks</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest">Received</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((s) => {
              const fl = subFiles(s);
              return (
              <TableRow key={s.id} data-testid={`ad-submission-row-${s.id}`}>
                <TableCell>
                  <div className="font-semibold text-sm">{s.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{s.company}</div>
                  <div className="font-mono text-xs text-muted-foreground">{s.email}</div>
                  {s.sites && (
                    <div className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-foreground/60" data-testid={`ad-sites-${s.id}`}>
                      Runs on: {SITE_LABELS[s.sites] || s.sites}
                    </div>
                  )}
                  {s.url_wsa && <div className="font-mono text-[10px] text-muted-foreground break-all max-w-[220px]">WSA → {s.url_wsa}</div>}
                  {s.url_sc && <div className="font-mono text-[10px] text-muted-foreground break-all max-w-[220px]">SC → {s.url_sc}</div>}
                </TableCell>
                <TableCell>
                  <div className="space-y-3">
                    {fl.map((f, i) => (
                      <div key={i} className="flex items-center gap-3" data-testid={`ad-file-${s.id}-${i}`}>
                        <AdThumb subId={s.id} idx={i} alt={f.filename} />
                        <div>
                          <div className="font-mono text-xs flex items-center gap-2">
                            {f.filename}
                            <button onClick={() => downloadAdFile(s, i, f.filename)} data-testid={`ad-download-${s.id}-${i}`} className="text-primary hover:text-ink" title="Download this file">
                              <Download size={13} />
                            </button>
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground" data-testid={`ad-dims-${s.id}-${i}`}>
                            {f.format} · {f.width}×{f.height}
                            {f.matched_size ? ` · Elegant Size ${f.matched_size}` : ""}
                            {f.frames > 1 ? ` · ${f.frames} frames` : ""}
                            {f.size_bytes ? ` · ${(f.size_bytes / 1048576).toFixed(2)} MB` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-800" data-testid={`ad-checks-${s.id}`}>
                    {fl.length} {fl.length === 1 ? "file" : "files"} · all checks passed
                  </span>
                </TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap">
                  {(s.created_at || "").replace("T", " ").slice(0, 16)}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <button onClick={() => downloadAdZip(s)} data-testid={`ad-zip-${s.id}`} className="text-primary hover:underline inline-flex items-center gap-1 mr-4 font-mono text-xs">
                    <Download size={14} /> Download zip
                  </button>
                  <button onClick={() => del(s)} data-testid={`ad-delete-${s.id}`} className="text-muted-foreground hover:text-destructive p-2">
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
              );
            })}
            {list.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-muted-foreground font-light">
                  No ad uploads yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const TABS = [
  { v: "overview", label: "Overview", C: Overview },
  { v: "brands", label: "Brands", C: BrandsTab },
  { v: "leaders", label: "Leadership", C: LeadersTab },
  { v: "jobs", label: "Jobs", C: JobsTab },
  { v: "news", label: "News", C: NewsTab },
  { v: "contacts", label: "Enquiries", C: Contacts },
  { v: "applications", label: "Applications", C: Applications },
  { v: "ads", label: "Ad uploads", C: AdUploadsTab },
  { v: "subscribers", label: "Subscribers", C: Subscribers },
  { v: "backups", label: "Backups", C: Backups },
];

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur-xl z-40">
        <div className="px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="font-serif text-2xl">Which</span>
            <span className="font-serif italic text-2xl text-primary">Media</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground ml-3">CMS</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/" target="_blank" rel="noopener noreferrer" className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-2">
              View site <ExternalLink size={14} />
            </a>
            <span className="hidden md:block font-mono text-xs text-muted-foreground">{user?.email}</span>
            <button onClick={doLogout} data-testid="logout-btn" className="text-muted-foreground hover:text-destructive flex items-center gap-2 font-mono text-xs uppercase tracking-widest">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-10 py-10 max-w-[1500px] mx-auto">
        <Tabs defaultValue="overview">
          <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 mb-10 overflow-x-auto flex-nowrap">
            {TABS.map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`tab-${t.v}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary font-mono text-xs uppercase tracking-widest px-5 py-3 whitespace-nowrap"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t.v} value={t.v} className="mt-0">
              <t.C />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
